import { z } from 'zod';
/**
 * Venue map contracts — the physical shape of a room (SDAD Section 16, as
 * superseded by ADR 0036).
 *
 * This file is the agreed boundary between the server's map authority and the
 * organizer map editor and renderer. Both sides compile against these types, so
 * a shape one side invents is a shape the other refuses.
 *
 * Two things this contract deliberately does **not** describe:
 *
 *   - **Availability.** Nothing here says whether a seat can be bought. Seat
 *     commerce extends the single inventory authority (ADR 0015) in a later
 *     tranche; a renderer that inferred "sellable" from a map would be
 *     inventing a second inventory, which is exactly the failure this split
 *     exists to prevent.
 *   - **Entitlement.** No access zone, ticket product, price or pool appears.
 *     A venue map is reusable across every event held in the room, and welding
 *     it to one show's entitlement model would make it reusable across none.
 */
// --- Geometry ---------------------------------------------------------------
/**
 * The version of the geometry vocabulary below.
 *
 * Stamped onto every map version, so a renderer that is several releases behind
 * the server can refuse a shape it does not understand rather than draw
 * something plausible and wrong. Bumping this is a contract change and needs
 * both sides; adding an optional field to an existing shape is not.
 */
export const GEOMETRY_SCHEMA_VERSION = 1;
export const geometrySchemaVersionSchema = z.literal(GEOMETRY_SCHEMA_VERSION);
/**
 * A canonical topology digest — sha-256 over the server's normalised
 * projection of a version's topology.
 *
 * The server owns this calculation. It is **not** a hash of the submitted
 * request bytes: field order, collection order, absent-versus-null and numeric
 * formatting all vary between clients, and a digest that moved with any of them
 * would be useless as a concurrency token. The server normalises all four, then
 * hashes.
 *
 * A client may carry it and compare it. A client may not compute it.
 */
export const topologyDigestSchema = z.string().regex(/^[0-9a-f]{64}$/);
/**
 * A coordinate in the map version's logical canvas.
 *
 * **Integer logical design units, never device pixels and never floats.** Two
 * separate reasons, and both matter:
 *
 *   - A map authored on a desktop and rendered on a phone is the same map. The
 *     renderer scales the canvas; no stored coordinate depends on who drew it.
 *   - The topology digest has to be stable. `0.1 + 0.2` does not round-trip
 *     through JSON identically on every runtime, so a float coordinate makes
 *     "did this document change?" depend on which client serialised it. An
 *     integer grid removes the question rather than papering over it.
 *
 * An author who needs finer resolution increases the canvas, which is the same
 * thing a CAD grid does.
 */
const coordinateSchema = z.number().int().min(-1_000_000).max(1_000_000);
const extentSchema = z.number().int().positive().max(1_000_000);
/**
 * Rotation clockwise, in **millidegrees** — 90° is `90_000`.
 *
 * An integer unit for the same determinism reason as coordinates, and fine
 * enough that no venue has ever needed more: a thousandth of a degree across a
 * hundred-metre bowl is under two millimetres.
 */
const rotationSchema = z.number().int().min(-360_000).max(360_000);
export const pointSchema = z.object({ x: coordinateSchema, y: coordinateSchema });
/** A circular mark — how a single chair is normally drawn. */
export const pointShapeSchema = z.object({
    kind: z.literal('POINT'),
    x: coordinateSchema,
    y: coordinateSchema,
    radius: extentSchema.max(1_000).optional(),
    rotation: rotationSchema.optional(),
});
/** An axis-aligned box before rotation — a stand, a block, a rectangular table. */
export const rectShapeSchema = z.object({
    kind: z.literal('RECT'),
    x: coordinateSchema,
    y: coordinateSchema,
    width: extentSchema,
    height: extentSchema,
    rotation: rotationSchema.optional(),
});
/** A round table, or a curved stand approximated as one. */
export const circleShapeSchema = z.object({
    kind: z.literal('CIRCLE'),
    x: coordinateSchema,
    y: coordinateSchema,
    radius: extentSchema,
});
/** Anything a rectangle cannot honestly describe — a raked tier, a wedge. */
export const polygonShapeSchema = z.object({
    kind: z.literal('POLYGON'),
    points: z.array(pointSchema).min(3).max(512),
});
/** A run of seats drawn as a line — the usual shape of a row. */
export const polylineShapeSchema = z.object({
    kind: z.literal('POLYLINE'),
    points: z.array(pointSchema).min(2).max(512),
});
/**
 * The closed set of shapes a map may carry.
 *
 * Discriminated on `kind` so an unknown shape fails validation at the boundary
 * instead of reaching a renderer that will silently skip it. Free-form JSON was
 * considered and rejected: geometry that cannot be validated cannot be evolved.
 */
export const geometrySchema = z.discriminatedUnion('kind', [
    pointShapeSchema,
    rectShapeSchema,
    circleShapeSchema,
    polygonShapeSchema,
    polylineShapeSchema,
]);
/**
 * The logical drawing surface every geometry in a version is expressed in.
 *
 * Integer extents, declared per version and frozen when the version publishes.
 * Resizing the canvas under a frozen map would move every seat on it.
 */
export const canvasSchema = z.object({
    width: extentSchema,
    height: extentSchema,
});
// --- Identifiers ------------------------------------------------------------
/** A map's stable machine name, in the same alphabet as zone and pool codes. */
export const venueMapCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .min(2)
    .max(39)
    .regex(/^[A-Z][A-Z0-9_]*$/, 'Use capitals, numbers and underscores, starting with a letter.');
/** A section or seat-group code. Same alphabet, allowed to be a single letter. */
export const topologyCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .min(1)
    .max(40)
    .regex(/^[A-Z][A-Z0-9_]*$/, 'Use capitals, numbers and underscores, starting with a letter.');
/**
 * A row or table label as humans actually write them: "AA", "12", "J", "T4".
 * Deliberately laxer than a code — insisting a row be called ROW_AA would make
 * every real import a translation exercise.
 */
export const topologyLabelSchema = z.string().trim().min(1).max(40);
/**
 * The natural key of a physical chair, stable for the life of the map.
 *
 * This is the string that makes a re-import find the same chair rather than
 * inventing a second one, and it is what a durable seat identity is keyed on.
 * Organizers supply it from their own seating data — conventionally
 * `SECTION/ROW/SEAT` — and once a version referencing it is published, changing
 * it means a different chair.
 */
export const seatKeySchema = z.string().trim().min(1).max(120);
// --- Topology document ------------------------------------------------------
export const sectionKindSchema = z.enum(['SEATED', 'TABLE', 'STANDING']);
/**
 * What kind of accessible provision a chair is.
 *
 * Typed rather than a bare boolean, because "accessible" alone does not let
 * anybody choose. A wheelchair space has no chair in it; a transfer seat has
 * one with room to move beside it; an ambulant seat is an ordinary chair with
 * fewer steps to reach it. Someone picking between them is answering a
 * different question each time, and a single flag answers none of them.
 *
 * `COMPANION_SEAT` is the chair kept for whoever comes with the person using
 * the accessible place. It is accessible provision in the sense that it is not
 * ordinary stock, and it is not itself a place for a wheelchair.
 */
export const accessibilityKindSchema = z.enum([
    'WHEELCHAIR_SPACE',
    'TRANSFER_SEAT',
    'AMBULANT_SEAT',
    'COMPANION_SEAT',
]);
export const seatGroupKindSchema = z.enum(['BOX', 'SUITE', 'POD', 'BLOCK']);
export const topologyRowSchema = z.object({
    code: topologyLabelSchema,
    displayName: z.string().trim().min(1).max(80),
    displayOrder: z.number().int().min(0).max(100_000).default(0),
    geometry: geometrySchema.nullish(),
});
export const topologyTableSchema = z.object({
    code: topologyLabelSchema,
    displayName: z.string().trim().min(1).max(80),
    /** How many chairs the furniture holds. Not a commercial quantity. */
    seatCapacity: z.number().int().min(0).max(100),
    displayOrder: z.number().int().min(0).max(100_000).default(0),
    geometry: geometrySchema.nullish(),
});
export const topologySectionSchema = z.object({
    code: topologyCodeSchema,
    displayName: z.string().trim().min(1).max(120),
    kind: sectionKindSchema,
    displayOrder: z.number().int().min(0).max(100_000).default(0),
    geometry: geometrySchema.nullish(),
    rows: z.array(topologyRowSchema).max(2000).default([]),
    tables: z.array(topologyTableSchema).max(2000).default([]),
});
/**
 * One chair, placed.
 *
 * `rowCode` xor `tableCode`: a chair stands in a row or at a table. The
 * placement must suit the section — a table seat in a SEATED section is refused
 * by the server and by the database underneath it.
 */
export const topologySeatSchema = z
    .object({
    seatKey: seatKeySchema,
    seatLabel: topologyLabelSchema,
    sectionCode: topologyCodeSchema,
    rowCode: topologyLabelSchema.nullish(),
    tableCode: topologyLabelSchema.nullish(),
    /** Physical facts about the chair. Neither implies a price. */
    isAccessible: z.boolean().default(false),
    /**
     * Required whenever `isAccessible` is true, and forbidden otherwise.
     *
     * A deliberate tightening: an accessible chair with no kind is the untyped
     * version this slice exists to replace, and it would reach a customer as a
     * seat their filter matched and their needs may not. The database holds the
     * same rule, so this is here to make it a named validation error rather than
     * a constraint violation.
     */
    accessibilityKind: accessibilityKindSchema.nullish(),
    hasObstructedView: z.boolean().default(false),
    displayOrder: z.number().int().min(0).max(100_000).default(0),
    geometry: geometrySchema.nullish(),
})
    .refine((seat) => seat.isAccessible === (seat.accessibilityKind != null), {
    message: 'An accessible seat must say what kind of provision it is, and an ordinary seat must not',
    path: ['accessibilityKind'],
});
/**
 * One explicit companion relationship.
 *
 * Authored, never inferred. Two chairs are not companions because their
 * coordinates are close: an aisle, a barrier or a step can sit between them and
 * the canvas does not know. Only somebody who has walked the room knows, so
 * only they may say it.
 *
 * Keyed on seat keys rather than ids, like the rest of the topology document,
 * so a whole room can be imported in one pass before any chair has an id.
 */
export const topologyCompanionSchema = z.object({
    accessibleSeatKey: seatKeySchema,
    companionSeatKey: seatKeySchema,
});
export const topologySeatGroupSchema = z.object({
    code: topologyCodeSchema,
    displayName: z.string().trim().min(1).max(120),
    kind: seatGroupKindSchema.default('BLOCK'),
    displayOrder: z.number().int().min(0).max(100_000).default(0),
    geometry: geometrySchema.nullish(),
    /** The chairs the room groups together. Membership, not a sale rule. */
    seatKeys: z.array(seatKeySchema).min(1).max(5000),
});
/**
 * A complete physical topology for one draft version.
 *
 * Import is a whole-document replace rather than a patch stream, and that is a
 * ruling rather than a convenience: a partial edit language would need conflict
 * rules, and a half-applied map is worse than a rejected one. Replaying the
 * same document is a no-op — the same chairs keep the same durable identities.
 */
export const venueMapTopologySchema = z.object({
    schemaVersion: geometrySchemaVersionSchema,
    canvas: canvasSchema,
    sections: z.array(topologySectionSchema).min(1).max(500),
    seats: z.array(topologySeatSchema).max(100_000).default([]),
    seatGroups: z.array(topologySeatGroupSchema).max(2000).default([]),
    /** Explicit accessible/companion pairs. Empty is the ordinary case. */
    companions: z.array(topologyCompanionSchema).max(5000).default([]),
});
// --- Refusal reasons --------------------------------------------------------
/**
 * Why a venue-map command was refused, as a stable machine code.
 *
 * `RESOURCE_CONFLICT` says only "the resource is in the wrong state", which is
 * true of every refusal below and useful for none of them. An Organizer client
 * has to offer a remedy, and the remedy differs: open the draft you already
 * have, pick a different map, reload, or import a topology first. Making the
 * client infer that from prose would mean a copy edit could silently change
 * which button appears.
 *
 * The HTTP status stays the conflict status. This travels beside it, as the
 * `reasonCode` on the error body.
 *
 * Deliberately four codes and not fourteen. A code exists here only where the
 * *remedy* genuinely differs — the four places a version turns out not to be a
 * draft all share one code, because in all four the answer is the same.
 *
 * A stale topology write is **not** in this list. It carries its own top-level
 * `VENUE_MAP_TOPOLOGY_STALE` error code and a 412, because it is a precondition
 * failure rather than a state conflict, and a client branching on `code` should
 * catch it before it ever looks at a reason.
 */
export const VENUE_MAP_REFUSAL_REASONS = [
    /**
     * A map may have one open draft at a time, and it already has one.
     * Remedy: open that draft, or publish or withdraw it first.
     * Details carry `venueMapVersionId` and `versionNumber` so the client can
     * link straight to it rather than making the organizer go and find it.
     */
    'VENUE_MAP_OPEN_DRAFT_EXISTS',
    /**
     * The map is retired and no longer accepts new authoring.
     * Remedy: author against an active map. Its published versions stay valid —
     * this refuses new work, it does not invalidate old work.
     */
    'VENUE_MAP_RETIRED',
    /**
     * The command needs a draft and this version is not one. Covers importing
     * into, publishing, or withdrawing a version that has been published,
     * superseded or withdrawn — including losing a race to another writer.
     * Remedy: reload; open a new draft if an edit was intended.
     * Details carry `lifecycle`.
     */
    'VENUE_MAP_VERSION_NOT_DRAFT',
    /**
     * Publication was asked for on a version with no seats. An empty room would
     * give later inventory an authority it cannot tell apart from a genuinely
     * standing-only one.
     * Remedy: import a topology, then publish.
     */
    'VENUE_MAP_VERSION_HAS_NO_SEATS',
];
export const venueMapRefusalReasonSchema = z.enum(VENUE_MAP_REFUSAL_REASONS);
/**
 * Narrows an error body's `reasonCode` to one this client knows.
 *
 * Returns `null` for anything unrecognised, so a client several releases behind
 * a new reason falls back to the generic refusal rather than matching nothing
 * and rendering an empty state.
 */
export function asVenueMapRefusalReason(value) {
    const parsed = venueMapRefusalReasonSchema.safeParse(value);
    return parsed.success ? parsed.data : null;
}
// --- Requests ---------------------------------------------------------------
export const createVenueMapRequestSchema = z.object({
    code: venueMapCodeSchema,
    displayName: z.string().trim().min(1).max(120),
});
/**
 * Open a new draft.
 *
 * Cloning is how a room is corrected: last season's published version becomes
 * this season's starting point, and the published one it came from is never
 * touched.
 */
export const createDraftVersionRequestSchema = z.object({
    cloneFromVersionId: z.string().uuid().nullish(),
    canvas: canvasSchema.nullish(),
});
/**
 * Replace a draft's topology, with an optimistic precondition.
 *
 * `expectedTopologyDigest` is the digest the editor loaded. If the persisted
 * topology has moved since — another organizer saved, another tab saved, the
 * same person saved from a stale window — the write is refused as
 * `VENUE_MAP_TOPOLOGY_STALE` and the editor shows a visible stale state rather
 * than quietly overwriting somebody's afternoon.
 *
 * Over HTTP this arrives as `If-Match: "<digest>"`, and the adapter maps the
 * header onto this field. The typed field exists so the domain command carries
 * its own precondition rather than depending on a transport header.
 *
 * There is deliberately no way to opt out. 10A does not merge.
 */
export const importTopologyRequestSchema = z.object({
    topology: venueMapTopologySchema,
    expectedTopologyDigest: topologyDigestSchema,
});
// --- Responses --------------------------------------------------------------
export const venueMapVersionLifecycleSchema = z.enum([
    'DRAFT',
    'PUBLISHED',
    'SUPERSEDED',
    'WITHDRAWN',
]);
export const venueMapVersionSummarySchema = z.object({
    venueMapVersionId: z.string().uuid(),
    versionNumber: z.number().int().min(1),
    lifecycle: venueMapVersionLifecycleSchema,
    canvas: canvasSchema,
    geometrySchemaVersion: z.number().int().min(1),
    /**
     * The server's canonical digest of this version's topology.
     *
     * Always present, including for a freshly opened draft — an empty room has a
     * digest like any other, which is what lets the first save carry a
     * precondition instead of being the one write with no protection. Served as
     * the `ETag` on a topology read.
     */
    topologyDigest: topologyDigestSchema,
    seatCount: z.number().int().min(0),
    createdAt: z.string(),
    publishedAt: z.string().nullable(),
    supersededAt: z.string().nullable(),
    withdrawnAt: z.string().nullable(),
});
export const venueMapSummarySchema = z.object({
    venueMapId: z.string().uuid(),
    venueId: z.string().uuid(),
    code: z.string(),
    displayName: z.string(),
    status: z.enum(['ACTIVE', 'RETIRED']),
    createdAt: z.string(),
    /** The version an editor would open, if there is one. */
    draftVersion: venueMapVersionSummarySchema.nullable(),
    /** The version later authority resolves as "the map". */
    publishedVersion: venueMapVersionSummarySchema.nullable(),
});
export const venueMapDetailSchema = venueMapSummarySchema.extend({
    versions: z.array(venueMapVersionSummarySchema),
});
/**
 * A seat as the server knows it.
 *
 * Both identifiers are present and both matter. `seatId` is this version's
 * occurrence and is what later inventory pins; `seatIdentityId` is the chair
 * across every version and is what a returning holder means. A client that
 * keeps only the second will misread a re-mapped room.
 */
export const venueMapSeatSchema = z.object({
    seatId: z.string().uuid(),
    seatIdentityId: z.string().uuid(),
    seatKey: z.string(),
    seatLabel: z.string(),
    sectionId: z.string().uuid(),
    rowId: z.string().uuid().nullable(),
    tableId: z.string().uuid().nullable(),
    isAccessible: z.boolean(),
    accessibilityKind: accessibilityKindSchema.nullable(),
    hasObstructedView: z.boolean(),
    displayOrder: z.number().int(),
    geometry: geometrySchema.nullable(),
});
export const venueMapRowViewSchema = z.object({
    rowId: z.string().uuid(),
    code: z.string(),
    displayName: z.string(),
    displayOrder: z.number().int(),
    geometry: geometrySchema.nullable(),
});
export const venueMapTableViewSchema = z.object({
    tableId: z.string().uuid(),
    code: z.string(),
    displayName: z.string(),
    seatCapacity: z.number().int(),
    displayOrder: z.number().int(),
    geometry: geometrySchema.nullable(),
});
export const venueMapSectionViewSchema = z.object({
    sectionId: z.string().uuid(),
    code: z.string(),
    displayName: z.string(),
    kind: sectionKindSchema,
    displayOrder: z.number().int(),
    geometry: geometrySchema.nullable(),
    rows: z.array(venueMapRowViewSchema),
    tables: z.array(venueMapTableViewSchema),
});
export const venueMapSeatGroupViewSchema = z.object({
    seatGroupId: z.string().uuid(),
    code: z.string(),
    displayName: z.string(),
    kind: seatGroupKindSchema,
    displayOrder: z.number().int(),
    geometry: geometrySchema.nullable(),
    seatIds: z.array(z.string().uuid()),
});
/**
 * A whole version, ready to render.
 *
 * Flat seat array rather than seats nested under rows and tables: a renderer
 * draws one pass over every chair, and a later inventory overlay joins on
 * `seatId` without walking a tree.
 */
export const venueMapVersionTopologySchema = z.object({
    venueMapId: z.string().uuid(),
    venueMapCode: z.string(),
    version: venueMapVersionSummarySchema,
    sections: z.array(venueMapSectionViewSchema),
    seats: z.array(venueMapSeatSchema),
    seatGroups: z.array(venueMapSeatGroupViewSchema),
    /**
     * Companion pairs, by occurrence id.
     *
     * On the read side these are ids because a renderer already holds the chairs
     * by id; the authoring document uses seat keys, because a whole room is
     * imported before any chair has one.
     */
    companions: z.array(z.object({
        accessibleSeatId: z.string().uuid(),
        companionSeatId: z.string().uuid(),
    })),
});
export const venueMapVersionEventSchema = z.object({
    eventType: z.enum(['DRAFT_CREATED', 'TOPOLOGY_IMPORTED', 'PUBLISHED', 'WITHDRAWN', 'SUPERSEDED']),
    occurredAt: z.string(),
    actorUserId: z.string().uuid().nullable(),
    detail: z.record(z.string(), z.unknown()),
});
//# sourceMappingURL=venue-map-schemas.js.map